import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useMemo, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useCompConfig } from "@/context/CompConfigContext";
import useBangKeoMatches from "@/hooks/useBangKeoMatches";
import Loading from "@/components/Loading";
import NoData from "@/components/NoData";

import KdbMatch from "../KdbMatch";
import KdbMatchLeagues from "../KdbMatchLeagues";
import KdbDateTabs from "../KdbDateTabs";
import LeagueFilterV22 from "../LeagueFilterV22";
import KdbOddsAndBookmaker from "../KdbOddsAndBookmaker";
import LoadMoreButton from "../LoadMoreButton";
import { BANG_KEO_PAGE_LIMIT, BANKER, BANKER_EXTENDS } from "@/contants";
import { useLiveData } from "@/hooks/useLiveData";
import { useLiveUpdates } from "@/hooks/useLiveUpdates";
import {
  CHANGE_BOOKMAKER,
  CHANGE_BOOKMAKER_EXTEND,
} from "@/lib/redux/reducers/actionTypes";

dayjs.extend(utc);
dayjs.extend(timezone);

const TZ = "Asia/Ho_Chi_Minh";

const KbvTiLeV22 = ({ type = "all" }) => {

  const {
    colorPrimary,
    colorSecondary,
    colorBg,
    colorText,
    colorForeground,
    colorOnPrimary,
    colorBorder,
    colorTextMuted,
    colorAccent,
    colorForeground60,
    colorTabHover,
    colorBtnBorder,
    colorTabActive,
    colorTabInactive,
    colorTabText,
    colorLiveChipBg,
    colorFilterActiveBg,
    colorTableHeaderBg,
    colorTableHeaderText,
    hasDataConfig,
  } = useCompConfig();

  const themeStyle = hasDataConfig
    ? {
      "--theme-primary": colorPrimary,
      "--theme-highlight": colorSecondary,
      "--theme-bg": colorBg,
      "--theme-text": colorText,
      "--theme-foreground": colorForeground,
      ...(colorOnPrimary != null && { "--theme-on-primary": colorOnPrimary }),
      ...(colorBorder != null && { "--theme-border": colorBorder }),
      ...(colorBtnBorder != null ? { "--theme-btn-border": colorBtnBorder } : colorBorder != null ? { "--theme-btn-border": colorBorder } : {}),
      ...(colorTextMuted != null && { "--theme-text-muted": colorTextMuted }),
      ...(colorTextMuted != null && { "--text-half-score": colorTextMuted }),
      ...(colorAccent != null && { "--theme-accent": colorAccent }),
      ...(colorForeground60 != null && { "--theme-foreground-60": colorForeground60 }),
      ...(colorTabHover != null && { "--theme-tab-hover": colorTabHover }),
      ...(colorTabActive != null && { "--theme-tab-active": colorTabActive }),
      ...(colorTabInactive != null && { "--theme-tab-inactive": colorTabInactive }),
      ...(colorTabText != null && { "--theme-tab-text": colorTabText }),
      ...(colorLiveChipBg != null && { "--theme-live-chip-bg": colorLiveChipBg }),
      ...(colorFilterActiveBg != null && { "--theme-filter-active-bg": colorFilterActiveBg }),
      ...(colorTableHeaderBg != null && { "--theme-table-header-bg": colorTableHeaderBg }),
      ...(colorTableHeaderText != null && { "--theme-table-header-text": colorTableHeaderText }),
    }
    : {};

  const dispatch = useDispatch();
  const bookmarkerExtend = useSelector(
    (state) => state.bookmarkerExtendReducer,
  );
  const bookmarker = useSelector((state) => state.bookmakerReducer);

  const bookmakerOptions = useMemo(
    () => [
      { value: "-1", label: "Tất cả" },
      ...BANKER.map((item) => ({ value: item.value, label: item.name })),
    ],
    [],
  );

  const bookmakerExtendOptions = useMemo(
    () =>
      BANKER_EXTENDS.map((item) => ({
        value: item.value,
        label: `Tỷ lệ ${item.shortName}`,
      })),
    [],
  );

  const today = dayjs().tz(TZ);
  const todayString = today.format("YYYY-MM-DD");
  const [firstLoad, setFirstLoad] = useState(true);
  const [selectedDate, setSelectedDate] = useState(todayString);
  const [isHotOnly, setIsHotOnly] = useState(false);
  const [selectedLeagueIds, setSelectedLeagueIds] = useState([]);
  const [displayLimit, setDisplayLimit] = useState(20);
  const [oddType, setOddType] = useState("hdp");

  const dateString =
    !selectedDate || selectedDate === ""
      ? "all"
      : selectedDate === "live"
        ? todayString
        : selectedDate;
  const status =
    type === "ketqua" ? "end" : selectedDate === "live" ? "live" : "";

  const queryObject = useMemo(
    () => ({
      include: "homeTeam,awayTeam,odd,countOtherOdds,stats,events,insight",
      filterInclude: `date:${dateString}`,
      filterOdds: -1,
      cache: false,
      status,
      page: 1,
      limit: BANG_KEO_PAGE_LIMIT,
    }),
    [dateString, status],
  );

  const {
    hotLeaguesWithMatches,
    leaguesWithMatches,
    loading,
    error,
    hasMore,
    isLoadingMore,
    handleLoadMore,
  } = useBangKeoMatches(queryObject, false);

  const normalizedLeaguesWithMatches = useMemo(
    () =>
      (leaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [leaguesWithMatches],
  );

  const normalizedHotLeaguesWithMatches = useMemo(
    () =>
      (hotLeaguesWithMatches || []).map((item) => {
        const normalizedMatches = Array.isArray(item?.matches)
          ? item.matches
          : Object.values(item?.matches || {});
        return {
          ...item,
          matches: normalizedMatches.filter(Boolean),
        };
      }),
    [hotLeaguesWithMatches],
  );

  const baseLeaguesWithMatches = useMemo(
    () =>
      isHotOnly ? normalizedHotLeaguesWithMatches : normalizedLeaguesWithMatches,
    [isHotOnly, normalizedHotLeaguesWithMatches, normalizedLeaguesWithMatches],
  );

  const filteredLeaguesWithMatches = useMemo(() => {
    if (!selectedLeagueIds?.length) return baseLeaguesWithMatches;
    const idSet = new Set(selectedLeagueIds.map(String));
    return baseLeaguesWithMatches.filter(({ league }) =>
      idSet.has(String(league?.id)),
    );
  }, [baseLeaguesWithMatches, selectedLeagueIds]);

  const allFilteredMatches = useMemo(() => {
    return filteredLeaguesWithMatches.flatMap(({ league, matches }) =>
      (matches || []).map((m) => ({ ...m, league })),
    );
  }, [filteredLeaguesWithMatches]);

  useEffect(() => {
    if (
      !isHotOnly &&
      allFilteredMatches.length < displayLimit + 1 &&
      hasMore &&
      !loading &&
      !isLoadingMore
    ) {
      handleLoadMore();
    }
  }, [
    isHotOnly,
    allFilteredMatches.length,
    displayLimit,
    hasMore,
    loading,
    isLoadingMore,
    handleLoadMore,
  ]);

  const matchIds = useMemo(
    () =>
      filteredLeaguesWithMatches.flatMap(
        (league) => league.matches?.map((match) => match.id) || [],
      ),
    [filteredLeaguesWithMatches],
  );

  const { clearAllData } = useLiveData();
  useLiveUpdates(matchIds);

  useEffect(() => {
    return () => { clearAllData(); };
  }, [clearAllData]);

  useEffect(() => {
    if (
      firstLoad &&
      selectedDate === "live" &&
      !loading &&
      filteredLeaguesWithMatches.length <= 0
    ) {
      setSelectedDate(todayString);
    }
  }, [firstLoad, selectedDate, loading, filteredLeaguesWithMatches, todayString]);

  const handleLeagueFilterChange = (ids) => {
    setSelectedLeagueIds(ids);
    setDisplayLimit(20);
  };

  const handleDateChange = (value) => {
    setIsHotOnly(false);
    setSelectedDate(value);
    setFirstLoad(false);
    setDisplayLimit(20);
  };

  const handleToggleHot = () => {
    setIsHotOnly((prev) => !prev);
    setDisplayLimit(20);
  };

  return (
    <div
      className="min-h-screen kdb-app-bg kdb-app-text"
      style={themeStyle}
    >
      <div className="mx-auto max-w-[1240px] p-1 md:p-6">

        <div className="hidden md:flex md:flex-col md:gap-2 mt-4 mb-[8px] kdb-tabs-container-bg max-w-full overflow-visible">
          <div className="w-full">
            <KdbDateTabs
              selectedDate={selectedDate}
              onDateChange={handleDateChange}
            />
          </div>

          <div className="flex w-full min-w-0 items-center gap-2 pb-1 md:pb-2 md:px-[10px]">
            <button
              type="button"
              onClick={handleToggleHot}
              className={`h-[36px] shrink-0 cursor-pointer rounded-[4px] border border-solid px-[20px] text-[14px] font-medium transition-all hover:opacity-90 ${isHotOnly
                ? "border-[var(--theme-primary)] bg-[var(--theme-primary)] text-white"
                : "kdb-dropdown-border bg-white text-[var(--theme-text)]"
                }`}
            >
              Trận Hot
            </button>
            <div className="shrink-0">
              <KdbOddsAndBookmaker
                extendValue={bookmarkerExtend}
                bookmakerValue={bookmarker}
                extendOptions={bookmakerExtendOptions}
                bookmakerOptions={bookmakerOptions}
                onExtendChange={(val) =>
                  dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
                }
                onBookmakerChange={(val) =>
                  dispatch({ type: CHANGE_BOOKMAKER, payload: val })
                }
                extendPlaceholder="Tỷ lệ HONGKONG"
                bookmakerPlaceholder="Tất cả"
              />
            </div>
            <div className="min-w-0 w-[170px] shrink-0">
              <LeagueFilterV22
                selectedIds={selectedLeagueIds}
                onChange={handleLeagueFilterChange}
                className="w-full"
                themeStyle={themeStyle}
              />
            </div>
          </div>
        </div>

        {/* Mobile Controls */}
        <div className="md:hidden flex flex-col gap-2 pt-[8px] mb-[12px]">
          <div className="flex flex-col gap-2">
            <div className="w-full">
              <KdbDateTabs
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                compact
              />
            </div>
            <div className="flex w-full min-w-0 flex-col items-stretch gap-2">
              <button
                type="button"
                onClick={handleToggleHot}
                className={`h-[36px] w-full cursor-pointer rounded-[4px] border border-solid px-[20px] text-[14px] font-medium transition-all hover:opacity-90 ${isHotOnly
                  ? "border-[var(--theme-primary)] bg-[var(--theme-primary)] text-white"
                  : "kdb-dropdown-border bg-white text-[var(--theme-text)]"
                  }`}
              >
                Trận Hot
              </button>
              <div className="min-w-0 flex-1">
                <LeagueFilterV22
                  selectedIds={selectedLeagueIds}
                  onChange={handleLeagueFilterChange}
                  className="w-full"
                  themeStyle={themeStyle}
                />
              </div>
              <div className="shrink-0">
                <KdbOddsAndBookmaker
                  extendValue={bookmarkerExtend}
                  bookmakerValue={bookmarker}
                  extendOptions={bookmakerExtendOptions}
                  bookmakerOptions={bookmakerOptions}
                  onExtendChange={(val) =>
                    dispatch({ type: CHANGE_BOOKMAKER_EXTEND, payload: val })
                  }
                  onBookmakerChange={(val) =>
                    dispatch({ type: CHANGE_BOOKMAKER, payload: val })
                  }
                  extendPlaceholder="Tỷ lệ HONGKONG"
                  bookmakerPlaceholder="Tất cả"
                />
              </div>
            </div>
            <div className="kdb-control-bar-bg p-3 flex justify-end gap-6 items-center">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="v-mb"
                  value="hdp"
                  checked={oddType === "hdp"}
                  onChange={(e) => setOddType(e.target.value)}
                  className="m-0 w-4 h-4 accent-blue-600"
                />
                <span className="font-bold text-[12px] text-black">HDP</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="v-mb"
                  value="1x2"
                  checked={oddType === "1x2"}
                  onChange={(e) => setOddType(e.target.value)}
                  className="m-0 w-4 h-4 accent-blue-600"
                />
                <span className="font-bold text-[12px] text-black">1X2</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="v-mb"
                  value="ou"
                  checked={oddType === "ou"}
                  onChange={(e) => setOddType(e.target.value)}
                  className="m-0 w-4 h-4 accent-blue-600"
                />
                <span className="font-bold text-[12px] text-black">Tài xỉu</span>
              </label>
            </div>
          </div>
        </div>

        {/* Match List */}
        {loading && <Loading />}
        {!loading && error && <NoData message="Lỗi tải dữ liệu trận đấu" />}
        {!loading && (
          <>
            {filteredLeaguesWithMatches.length === 0 ? (
              <div className="p-10">
                <NoData
                  message={
                    isLoadingMore
                      ? "Đang tìm thêm trận đấu phù hợp..."
                      : "Không tìm thấy trận đấu theo tiêu chí lọc"
                  }
                />
              </div>
            ) : (
              <>
                {/* MOBILE: match demo-1/index.html outer section */}
                <section className="space-y-0 lg:hidden">
                  {filteredLeaguesWithMatches.map(({ league, matches }) => (
                    <KdbMatchLeagues
                      key={league?.id || league?.name}
                      league={league}
                      matches={matches}
                      render={(match, idx) => (
                        <KdbMatch
                          key={match?.id || idx}
                          match={match}
                          leagueName={league?.nameVN || league?.name}
                          leagueLogo={league?.logo}
                          oddType={oddType}
                          index={idx}
                        />
                      )}
                    />
                  ))}
                </section>

                <section className="hidden lg:block">
                  <div className="overflow-hidden rounded-[4px] bg-white">
                    <table className="w-full border-collapse bg-white">
                      <thead>
                        <tr className="bg-white h-[45px] border-b border-solid border-[var(--theme-border)]">
                          <td colSpan="100%" className="px-4">
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-gray-500" viewBox="0 0 512 512" fill="currentColor">
                                <path d="M256 32C132.3 32 32 132.3 32 256s100.3 224 224 224 224-100.3 224-224S379.7 32 256 32zm0 400c-15.9 0-31.1-2.4-45.5-6.8l29-45.5h33l29 45.5c-14.4 4.4-29.6 6.8-45.5 6.8zM116.5 352.5l-44.5-29c-8.4-14.4-14.4-29.6-18-45.5l54-17.5V209.5l-54-17.5c3.6-15.9 9.6-31.1 18-45.5l44.5-29 33 54h113.5l33-54 44.5 29c8.4 14.4 14.4 29.6 18 45.5l-54 17.5v50.5l54 17.5c-3.6 15.9-9.6 31.1-18 45.5l-44.5 29-33-54h-113.5l-33 54z" />
                              </svg>
                              <span className="font-bold text-gray-700 text-[12px] tracking-wide">BÓNG ĐÁ</span>
                            </div>
                          </td>
                        </tr>
                        <tr className="bg-[var(--theme-table-header-bg)] h-[40px] text-[var(--theme-tab-text)] text-[14px] font-medium border-b border-solid border-[var(--theme-border)]">
                          <td className="w-[80px] text-center border-r border-solid border-[var(--theme-border)] p-0">Thời gian</td>
                          <td className="text-right pr-4 border-r border-solid border-[var(--theme-border)] p-0">Đội nhà</td>
                          <td className="w-[60px] text-center border-r border-solid border-[var(--theme-border)] p-0">Tỉ số</td>
                          <td className="text-left pl-4 border-r border-solid border-[var(--theme-border)] p-0">Đội khách</td>
                          <td className="w-[280px] border-r border-solid border-[var(--theme-border)] p-0">
                            <div className="flex items-center justify-center gap-4 font-bold text-black">
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" checked={oddType === "hdp"} onChange={() => setOddType("hdp")} name="odd-type-desktop" className="m-0 w-3 h-3 accent-blue-600" /> HDP
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" checked={oddType === "1x2"} onChange={() => setOddType("1x2")} name="odd-type-desktop" className="m-0 w-3 h-3 accent-blue-600" /> 1x2
                              </label>
                              <label className="flex items-center gap-1 cursor-pointer">
                                <input type="radio" checked={oddType === "ou"} onChange={() => setOddType("ou")} name="odd-type-desktop" className="m-0 w-3 h-3 accent-blue-600" /> Tài/Xỉu
                              </label>
                            </div>
                          </td>
                          <td className="w-[80px] text-center p-0 border-r border-solid border-[var(--theme-border)]">Dữ liệu</td>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredLeaguesWithMatches.map(({ league, matches }) => (
                          <KdbMatchLeagues
                            key={league?.id || league?.name}
                            league={league}
                            matches={matches}
                            render={(match, idx) => (
                              <KdbMatch
                                key={match?.id || idx}
                                match={match}
                                leagueName={league?.nameVN || league?.name}
                                leagueLogo={league?.logo}
                                oddType={oddType}
                                index={idx}
                              />
                            )}
                          />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </section>
              </>
            )}

            {!isHotOnly && (
              <LoadMoreButton
                isLoading={isLoadingMore}
                onClick={() => setDisplayLimit((prev) => prev + 20)}
                loadingComponent={<Loading />}
                hasMore={allFilteredMatches.length > displayLimit || hasMore}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default KbvTiLeV22;
